import { useEffect, useRef, useState, useCallback } from 'react';
import { db } from '../../services/firebase';
import {
  doc, setDoc, onSnapshot, collection, addDoc,
  serverTimestamp, deleteDoc
} from 'firebase/firestore';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff,
  Monitor, MonitorOff, MessageSquare, X, Send, Users,
  Hand, Maximize2, Minimize2, LayoutGrid, Layout,
  Pin, PinOff, Volume2, VolumeX, SmilePlus
} from 'lucide-react';

interface Participant {
  uid: string;
  displayName: string;
  photoURL?: string;
  stream?: MediaStream;
  micOn: boolean;
  camOn: boolean;
  isSpeaking: boolean;
  isLocal: boolean;
  isPinned?: boolean;
  isScreenShare?: boolean;
  handRaised?: boolean;
  reaction?: string;
}

interface Props {
  currentUser: any;
  groupId: string;
  groupName: string;
  members: { uid: string; displayName: string; photoURL?: string }[];
  callType: 'audio' | 'video';
  onEnd: () => void;
}

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

type LayoutMode = 'grid' | 'spotlight' | 'sidebar';

const REACTIONS = ['👋', '👍', '❤️', '😂', '🎉', '🙏'];

const getInitials = (name: string) => (name || 'U').slice(0, 2).toUpperCase();

export const GroupCall = ({ currentUser, groupId, groupName, callType, onEnd }: Props) => {
  const [participants, setParticipants] = useState<Map<string, Participant>>(new Map());
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(callType === 'video');
  const [screenOn, setScreenOn] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ uid: string; name: string; text: string; ts: number }[]>([]);
  const [chatText, setChatText] = useState('');
  const [duration, setDuration] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('grid');
  const [pinnedUid, setPinnedUid] = useState<string | null>(null);
  const [handRaised, setHandRaised] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [activeReaction, setActiveReaction] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [mutedRemotes, setMutedRemotes] = useState<Set<string>>(new Set());
  const [showControls, setShowControls] = useState(true);
  const [screenShareUid, setScreenShareUid] = useState<string | null>(null);

  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const pcsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const unsubsRef = useRef<(() => void)[]>([]);
  const durationRef = useRef<any>(null);
  const audioAnalysersRef = useRef<Map<string, { analyser: AnalyserNode; interval: any }>>(new Map());
  const controlsTimerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const callRoomId = `group_${groupId}`;

  const formatDuration = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
      : `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const getMedia = async () => {
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: callType === 'video' ? { width: 1280, height: 720, facingMode: 'user' } : false,
    });
    localStreamRef.current = stream;
    return stream;
  };

  const startVoiceDetection = (uid: string, stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const interval = setInterval(() => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        const speaking = avg > 15;
        setParticipants(prev => {
          const next = new Map(prev);
          const p = next.get(uid);
          if (p && p.isSpeaking !== speaking) next.set(uid, { ...p, isSpeaking: speaking });
          return next;
        });
      }, 100);
      audioAnalysersRef.current.set(uid, { analyser, interval });
    } catch (e) {}
  };

  const stopVoiceDetection = (uid: string) => {
    const entry = audioAnalysersRef.current.get(uid);
    if (entry) { clearInterval(entry.interval); audioAnalysersRef.current.delete(uid); }
  };

  const createPC = (remoteUid: string, localStream: MediaStream) => {
    const existing = pcsRef.current.get(remoteUid);
    if (existing) existing.close();

    const pc = new RTCPeerConnection(ICE_SERVERS);
    pcsRef.current.set(remoteUid, pc);

    localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

    pc.ontrack = (e) => {
      const stream = e.streams[0];
      setParticipants(prev => {
        const next = new Map(prev);
        const ex = next.get(remoteUid);
        if (ex) next.set(remoteUid, { ...ex, stream });
        return next;
      });
      startVoiceDetection(remoteUid, stream);
    };

    pc.onicecandidate = async (e) => {
      if (e.candidate) {
        await addDoc(
          collection(db, 'groupCalls', callRoomId, 'candidates', currentUser.uid, 'to', remoteUid),
          e.candidate.toJSON()
        );
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setParticipants(prev => {
          const next = new Map(prev);
          next.delete(remoteUid);
          return next;
        });
        stopVoiceDetection(remoteUid);
      }
    };

    return pc;
  };

  const joinRoom = async () => {
    const stream = await getMedia();

    setParticipants(prev => {
      const next = new Map(prev);
      next.set(currentUser.uid, {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'Tú',
        photoURL: currentUser.photoURL,
        stream,
        micOn: true,
        camOn: callType === 'video',
        isSpeaking: false,
        isLocal: true,
        handRaised: false,
      });
      return next;
    });

    startVoiceDetection(currentUser.uid, stream);

    await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), {
      uid: currentUser.uid,
      displayName: currentUser.displayName || 'Usuario',
      photoURL: currentUser.photoURL || null,
      micOn: true,
      camOn: callType === 'video',
      handRaised: false,
      isScreenSharing: false,
      joinedAt: serverTimestamp(),
    });

    const unsub = onSnapshot(
      collection(db, 'groupCalls', callRoomId, 'participants'),
      async (snap) => {
        for (const change of snap.docChanges()) {
          const data = change.doc.data();
          const uid = data.uid;
          if (uid === currentUser.uid) continue;

          if (change.type === 'added') {
            setParticipants(prev => {
              const next = new Map(prev);
              if (!next.has(uid)) {
                next.set(uid, {
                  uid,
                  displayName: data.displayName,
                  photoURL: data.photoURL,
                  micOn: data.micOn,
                  camOn: data.camOn,
                  isSpeaking: false,
                  isLocal: false,
                  handRaised: data.handRaised || false,
                });
              }
              return next;
            });

            if (data.isScreenSharing) setScreenShareUid(uid);

            if (currentUser.uid < uid) {
              await initiateConnection(uid, stream);
            } else {
              await waitForOffer(uid, stream);
            }
          }

          if (change.type === 'modified') {
            if (data.isScreenSharing) {
              setScreenShareUid(uid);
              if (layoutMode === 'grid') setLayoutMode('spotlight');
            } else if (!data.isScreenSharing) {
              setScreenShareUid(prev => prev === uid ? null : prev);
            }
            setParticipants(prev => {
              const next = new Map(prev);
              const p = next.get(uid);
              if (p) next.set(uid, { ...p, micOn: data.micOn, camOn: data.camOn, handRaised: data.handRaised || false });
              return next;
            });
          }

          if (change.type === 'removed') {
            setParticipants(prev => {
              const next = new Map(prev);
              next.delete(uid);
              return next;
            });
            stopVoiceDetection(uid);
            pcsRef.current.get(uid)?.close();
            pcsRef.current.delete(uid);
          }
        }
      }
    );
    unsubsRef.current.push(unsub);

    const chatUnsub = onSnapshot(
      collection(db, 'groupCalls', callRoomId, 'chat'),
      (snap) => {
        const msgs = snap.docs.map(d => d.data() as any).sort((a, b) => a.ts - b.ts);
        setChatMessages(msgs);
        if (!showChat) {
          const newMsgs = snap.docChanges().filter(c => c.type === 'added' && c.doc.data().uid !== currentUser.uid).length;
          if (newMsgs > 0) setUnreadChat(prev => prev + newMsgs);
        }
      }
    );
    unsubsRef.current.push(chatUnsub);

    durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
  };

  const initiateConnection = async (remoteUid: string, stream: MediaStream) => {
    const pc = createPC(remoteUid, stream);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await setDoc(
      doc(db, 'groupCalls', callRoomId, 'offers', `${currentUser.uid}_${remoteUid}`),
      { from: currentUser.uid, to: remoteUid, offer: { type: offer.type, sdp: offer.sdp } }
    );

    const unsub = onSnapshot(
      doc(db, 'groupCalls', callRoomId, 'answers', `${remoteUid}_${currentUser.uid}`),
      async (snap) => {
        if (snap.exists() && pc.signalingState !== 'closed') {
          const data = snap.data();
          if (pc.remoteDescription) return;
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          listenForCandidates(remoteUid, pc);
        }
      }
    );
    unsubsRef.current.push(unsub);
  };

  const waitForOffer = async (remoteUid: string, stream: MediaStream) => {
    const unsub = onSnapshot(
      doc(db, 'groupCalls', callRoomId, 'offers', `${remoteUid}_${currentUser.uid}`),
      async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const pc = createPC(remoteUid, stream);
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await setDoc(
          doc(db, 'groupCalls', callRoomId, 'answers', `${currentUser.uid}_${remoteUid}`),
          { from: currentUser.uid, to: remoteUid, answer: { type: answer.type, sdp: answer.sdp } }
        );
        listenForCandidates(remoteUid, pc);
      }
    );
    unsubsRef.current.push(unsub);
  };

  const listenForCandidates = (remoteUid: string, pc: RTCPeerConnection) => {
    const unsub = onSnapshot(
      collection(db, 'groupCalls', callRoomId, 'candidates', remoteUid, 'to', currentUser.uid),
      (snap) => {
        snap.docChanges().forEach(async change => {
          if (change.type === 'added' && pc.signalingState !== 'closed') {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(change.doc.data() as RTCIceCandidateInit));
            } catch (e) {}
          }
        });
      }
    );
    unsubsRef.current.push(unsub);
  };

  const leaveCall = async () => {
    clearInterval(durationRef.current);
    unsubsRef.current.forEach(u => u());
    unsubsRef.current = [];
    audioAnalysersRef.current.forEach(({ interval }) => clearInterval(interval));
    audioAnalysersRef.current.clear();
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    pcsRef.current.forEach(pc => pc.close());
    pcsRef.current.clear();
    try { await deleteDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid)); } catch (e) {}
    onEnd();
  };

  const toggleMic = async () => {
    const track = localStreamRef.current?.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      const newVal = !micOn;
      setMicOn(newVal);
      setParticipants(prev => {
        const next = new Map(prev);
        const p = next.get(currentUser.uid);
        if (p) next.set(currentUser.uid, { ...p, micOn: newVal });
        return next;
      });
      await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), { micOn: newVal }, { merge: true });
    }
  };

  const toggleCam = async () => {
    const track = localStreamRef.current?.getVideoTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      const newVal = !camOn;
      setCamOn(newVal);
      setParticipants(prev => {
        const next = new Map(prev);
        const p = next.get(currentUser.uid);
        if (p) next.set(currentUser.uid, { ...p, camOn: newVal });
        return next;
      });
      await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), { camOn: newVal }, { merge: true });
    }
  };

  const toggleScreen = async () => {
    if (!screenOn) {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { width: 1920, height: 1080, frameRate: 30 },
          audio: true,
        });
        screenStreamRef.current = screenStream;
        const screenTrack = screenStream.getVideoTracks()[0];

        pcsRef.current.forEach(pc => {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) sender.replaceTrack(screenTrack);
        });

        screenTrack.onended = () => stopScreen();
        setScreenOn(true);
        setScreenShareUid(currentUser.uid);
        setLayoutMode('spotlight');
        setPinnedUid(currentUser.uid);
        await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), { isScreenSharing: true }, { merge: true });
      } catch (e) {}
    } else {
      stopScreen();
    }
  };

  const stopScreen = async () => {
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    const camTrack = localStreamRef.current?.getVideoTracks()[0];
    if (camTrack) {
      pcsRef.current.forEach(pc => {
        const sender = pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(camTrack);
      });
    }
    setScreenOn(false);
    setScreenShareUid(null);
    if (pinnedUid === currentUser.uid) setPinnedUid(null);
    await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), { isScreenSharing: false }, { merge: true });
  };

  const toggleHandRaise = async () => {
    const newVal = !handRaised;
    setHandRaised(newVal);
    setParticipants(prev => {
      const next = new Map(prev);
      const p = next.get(currentUser.uid);
      if (p) next.set(currentUser.uid, { ...p, handRaised: newVal });
      return next;
    });
    await setDoc(doc(db, 'groupCalls', callRoomId, 'participants', currentUser.uid), { handRaised: newVal }, { merge: true });
  };

  const sendReaction = async (emoji: string) => {
    setActiveReaction(emoji);
    setShowReactions(false);
    setTimeout(() => setActiveReaction(null), 3000);
    await addDoc(collection(db, 'groupCalls', callRoomId, 'reactions'), {
      uid: currentUser.uid,
      name: currentUser.displayName,
      emoji,
      ts: Date.now(),
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePin = (uid: string) => {
    if (pinnedUid === uid) {
      setPinnedUid(null);
      setLayoutMode('grid');
    } else {
      setPinnedUid(uid);
      setLayoutMode('spotlight');
    }
  };

  const toggleMuteRemote = (uid: string) => {
    setMutedRemotes(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const sendChatMessage = async () => {
    if (!chatText.trim()) return;
    await addDoc(collection(db, 'groupCalls', callRoomId, 'chat'), {
      uid: currentUser.uid,
      name: currentUser.displayName || 'Usuario',
      text: chatText.trim(),
      ts: Date.now(),
    });
    setChatText('');
  };

  // Auto-hide controls
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 4000);
  }, []);

  useEffect(() => {
    joinRoom();
    return () => { leaveCall(); };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const participantList = Array.from(participants.values());
  const count = participantList.length;

  // Determine spotlight participant
  const spotlightUid = pinnedUid || screenShareUid ||
    (participantList.find(p => p.isSpeaking && !p.isLocal)?.uid) ||
    participantList[0]?.uid;
  const spotlightP = participants.get(spotlightUid || '');
  const sidebarParticipants = participantList.filter(p => p.uid !== spotlightUid);

  // Grid columns
  const gridCols = count <= 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;

  return (
    <div
      ref={containerRef}
      className="gcall-overlay"
      onMouseMove={resetControlsTimer}
      onTouchStart={resetControlsTimer}
    >
      <div className="gcall-wrap">

        {/* Header */}
        <div className={`gcall-header ${showControls ? 'gcall-header--visible' : 'gcall-header--hidden'}`}>
          <div className="gcall-header__info">
            <span className="gcall-header__name">{groupName}</span>
            <span className="gcall-header__duration">{formatDuration(duration)}</span>
            <span className="gcall-header__count"><Users size={13} /> {count}</span>
            {screenShareUid && (
              <span className="gcall-header__screenshare-badge">
                <Monitor size={12} /> Pantalla compartida
              </span>
            )}
          </div>
          <div className="gcall-header__actions">
            {/* Layout toggle */}
            <button
              className={`gcall-icon-btn ${layoutMode === 'spotlight' ? 'gcall-icon-btn--active' : ''}`}
              onClick={() => setLayoutMode(m => m === 'grid' ? 'spotlight' : 'grid')}
              title="Cambiar layout"
            >
              {layoutMode === 'grid' ? <Layout size={17} /> : <LayoutGrid size={17} />}
            </button>

            {/* Participants list */}
            <button
              className={`gcall-icon-btn ${showParticipants ? 'gcall-icon-btn--active' : ''}`}
              onClick={() => { setShowParticipants(p => !p); setShowChat(false); }}
              title="Participantes"
            >
              <Users size={17} />
            </button>

            {/* Chat */}
            <button
              className={`gcall-icon-btn ${showChat ? 'gcall-icon-btn--active' : ''}`}
              onClick={() => { setShowChat(p => !p); setShowParticipants(false); setUnreadChat(0); }}
              title="Chat"
            >
              <MessageSquare size={17} />
              {unreadChat > 0 && <span className="gcall-badge">{unreadChat}</span>}
            </button>

            {/* Fullscreen */}
            <button className="gcall-icon-btn" onClick={toggleFullscreen} title="Pantalla completa">
              {isFullscreen ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="gcall-body">

          {/* === GRID LAYOUT === */}
          {layoutMode === 'grid' && (
            <div className="gcall-grid" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
              {participantList.map(p => (
                <ParticipantTile
                  key={p.uid}
                  participant={p}
                  isMuted={mutedRemotes.has(p.uid)}
                  isPinned={pinnedUid === p.uid}
                  onPin={() => togglePin(p.uid)}
                  onMuteRemote={() => toggleMuteRemote(p.uid)}
                  showControls={showControls}
                />
              ))}
            </div>
          )}

          {/* === SPOTLIGHT LAYOUT === */}
          {layoutMode === 'spotlight' && (
            <div className="gcall-spotlight-wrap">
              {/* Main spotlight */}
              <div className="gcall-spotlight-main">
                {spotlightP && (
                  <ParticipantTile
                    participant={spotlightP}
                    isMuted={mutedRemotes.has(spotlightP.uid)}
                    isPinned={pinnedUid === spotlightP.uid}
                    onPin={() => togglePin(spotlightP.uid)}
                    onMuteRemote={() => toggleMuteRemote(spotlightP.uid)}
                    isSpotlight
                    showControls={showControls}
                  />
                )}
              </div>
              {/* Sidebar strip */}
              {sidebarParticipants.length > 0 && (
                <div className="gcall-spotlight-sidebar">
                  {sidebarParticipants.map(p => (
                    <ParticipantTile
                      key={p.uid}
                      participant={p}
                      isMuted={mutedRemotes.has(p.uid)}
                      isPinned={pinnedUid === p.uid}
                      onPin={() => togglePin(p.uid)}
                      onMuteRemote={() => toggleMuteRemote(p.uid)}
                      isSidebar
                      showControls={showControls}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === PARTICIPANTS PANEL === */}
          {showParticipants && (
            <div className="gcall-panel">
              <div className="gcall-panel__header">
                <span>Participantes ({count})</span>
                <button onClick={() => setShowParticipants(false)} className="gcall-icon-btn"><X size={16} /></button>
              </div>
              <div className="gcall-panel__list">
                {participantList.map(p => (
                  <div key={p.uid} className="gcall-participant-row">
                    <div className="gcall-participant-row__avatar">
                      {p.photoURL
                        ? <img src={p.photoURL} alt="" />
                        : <span>{getInitials(p.displayName)}</span>}
                      {p.isSpeaking && p.micOn && <span className="gcall-speaking-dot" />}
                    </div>
                    <span className="gcall-participant-row__name">
                      {p.isLocal ? 'Tú' : p.displayName}
                      {p.handRaised && <span className="gcall-hand-badge">✋</span>}
                    </span>
                    <div className="gcall-participant-row__icons">
                      {!p.micOn && <MicOff size={14} color="#ef4444" />}
                      {!p.camOn && <VideoOff size={14} color="#ef4444" />}
                      {!p.isLocal && (
                        <button
                          className="gcall-icon-btn"
                          style={{ width: 28, height: 28 }}
                          onClick={() => toggleMuteRemote(p.uid)}
                          title={mutedRemotes.has(p.uid) ? 'Activar audio' : 'Silenciar localmente'}
                        >
                          {mutedRemotes.has(p.uid) ? <VolumeX size={13} /> : <Volume2 size={13} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* === CHAT PANEL === */}
          {showChat && (
            <div className="gcall-chat">
              <div className="gcall-chat__header">
                <span>Chat de la llamada</span>
                <button onClick={() => setShowChat(false)} className="gcall-icon-btn"><X size={16} /></button>
              </div>
              <div className="gcall-chat__messages">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`gcall-chat__msg ${msg.uid === currentUser.uid ? 'gcall-chat__msg--own' : ''}`}>
                    {msg.uid !== currentUser.uid && <div className="gcall-chat__msg-name">{msg.name}</div>}
                    <div className="gcall-chat__msg-text">{msg.text}</div>
                    <div className="gcall-chat__msg-time">
                      {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
              <div className="gcall-chat__input">
                <input
                  value={chatText}
                  onChange={e => setChatText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Mensaje..."
                />
                <button onClick={sendChatMessage}><Send size={16} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Floating reaction display */}
        {activeReaction && (
          <div className="gcall-reaction-float">{activeReaction}</div>
        )}

        {/* Reactions picker */}
        {showReactions && (
          <div className="gcall-reactions-picker">
            {REACTIONS.map(e => (
              <button key={e} onClick={() => sendReaction(e)} className="gcall-reaction-btn">{e}</button>
            ))}
          </div>
        )}

        {/* Controls */}
        <div className={`gcall-controls ${showControls ? 'gcall-controls--visible' : 'gcall-controls--hidden'}`}>
          <button
            className={`gcall-ctrl-btn ${!micOn ? 'gcall-ctrl-btn--off' : ''}`}
            onClick={toggleMic}
            title={micOn ? 'Silenciar' : 'Activar mic'}
          >
            {micOn ? <Mic size={20} /> : <MicOff size={20} />}
            <span>{micOn ? 'Micrófono' : 'Silenciado'}</span>
          </button>

          {callType === 'video' && (
            <button
              className={`gcall-ctrl-btn ${!camOn ? 'gcall-ctrl-btn--off' : ''}`}
              onClick={toggleCam}
              title={camOn ? 'Apagar cámara' : 'Encender cámara'}
            >
              {camOn ? <Video size={20} /> : <VideoOff size={20} />}
              <span>{camOn ? 'Cámara' : 'Sin video'}</span>
            </button>
          )}

          <button
            className={`gcall-ctrl-btn ${screenOn ? 'gcall-ctrl-btn--active' : ''}`}
            onClick={toggleScreen}
            title="Compartir pantalla"
          >
            {screenOn ? <MonitorOff size={20} /> : <Monitor size={20} />}
            <span>{screenOn ? 'Dejar' : 'Compartir'}</span>
          </button>

          <button
            className={`gcall-ctrl-btn ${handRaised ? 'gcall-ctrl-btn--active' : ''}`}
            onClick={toggleHandRaise}
            title="Levantar mano"
          >
            <Hand size={20} />
            <span>{handRaised ? 'Bajar mano' : 'Mano'}</span>
          </button>

          <button
            className={`gcall-ctrl-btn ${showReactions ? 'gcall-ctrl-btn--active' : ''}`}
            onClick={() => setShowReactions(p => !p)}
            title="Reacciones"
          >
            <SmilePlus size={20} />
            <span>Reacción</span>
          </button>

          <button className="gcall-ctrl-btn gcall-ctrl-btn--end" onClick={leaveCall} title="Salir">
            <PhoneOff size={20} />
            <span>Salir</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Participant Tile ──
interface TileProps {
  participant: Participant;
  isMuted: boolean;
  isPinned: boolean;
  onPin: () => void;
  onMuteRemote: () => void;
  isSpotlight?: boolean;
  isSidebar?: boolean;
  showControls?: boolean;
}

const ParticipantTile = ({ participant: p, isMuted, isPinned, onPin, onMuteRemote, isSpotlight, isSidebar, showControls }: TileProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showTileMenu, setShowTileMenu] = useState(false);

  useEffect(() => {
    if (videoRef.current && p.stream) {
      videoRef.current.srcObject = p.stream;
    }
  }, [p.stream]);

  const tileClass = [
    'gcall-tile',
    p.isSpeaking && p.micOn ? 'gcall-tile--speaking' : '',
    isPinned ? 'gcall-tile--pinned' : '',
    isSpotlight ? 'gcall-tile--spotlight' : '',
    isSidebar ? 'gcall-tile--sidebar' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={tileClass} onMouseEnter={() => setShowTileMenu(true)} onMouseLeave={() => setShowTileMenu(false)}>
      {p.stream && p.camOn ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={p.isLocal || isMuted}
          className="gcall-tile__video"
        />
      ) : (
        <div className="gcall-tile__avatar">
          {p.photoURL
            ? <img src={p.photoURL} alt="" />
            : <span>{getInitials(p.displayName)}</span>}
        </div>
      )}

      {/* Tile overlay controls */}
      {showTileMenu && showControls && (
        <div className="gcall-tile__overlay-controls">
          <button className="gcall-tile__overlay-btn" onClick={onPin} title={isPinned ? 'Desanclar' : 'Anclar'}>
            {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
          </button>
          {!p.isLocal && (
            <button className="gcall-tile__overlay-btn" onClick={onMuteRemote} title={isMuted ? 'Escuchar' : 'Silenciar'}>
              {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          )}
        </div>
      )}

      <div className="gcall-tile__info">
        <span className="gcall-tile__name">{p.isLocal ? 'Tú' : p.displayName}</span>
        {!p.micOn && <MicOff size={12} className="gcall-tile__muted" />}
        {p.handRaised && <span className="gcall-tile__hand">✋</span>}
        {isMuted && !p.isLocal && <VolumeX size={12} style={{ color: '#f59e0b' }} />}
      </div>

      {p.reaction && (
        <div className="gcall-tile__reaction">{p.reaction}</div>
      )}
    </div>
  );
};